import { fromJS } from "immutable";
import { createNewReframe } from "../../helper/reframeHelper";

const getBaseVideoItem = (workspaceItems) => {
  return Object.values(workspaceItems).find((item) => item.type === "UPVIDEO" && !item.sceneId) || null;
};

/**
 * Computes workspace x/y/width/height for a short clip video item.
 * Uses object-fit: cover scaling (same as buildVideoBlobMeta) and centres
 * on the reframe focus point when reframe data is present.
 *
 * @param {object} baseVideoItem - Source UPVIDEO workspace item (for pwidth/pheight)
 * @param {object|null} reframe - { x, y } normalised focus centre (0–1), or null
 * @param {number} projectWidth
 * @param {number} projectHeight
 * @returns {{ x: number, y: number, width: number, height: number }}
 */
export const getReframeWorkspacePosition = (
  baseVideoItem,
  reframe,
  projectWidth,
  projectHeight
) => {
  const sourceWidth = baseVideoItem.pwidth || baseVideoItem.width;
  const sourceHeight = baseVideoItem.pheight || baseVideoItem.height;

  let scaledWidth = projectWidth;
  let scaledHeight = (sourceHeight / sourceWidth) * scaledWidth;

  if (scaledHeight < projectHeight) {
    scaledHeight = projectHeight;
    scaledWidth = (sourceWidth / sourceHeight) * scaledHeight;
  }

  let x = (projectWidth - scaledWidth) / 2;
  let y = (projectHeight - scaledHeight) / 2;

  if (reframe && Number.isFinite(reframe.x) && Number.isFinite(reframe.y)) {
    const focusX = reframe.x * scaledWidth;
    const focusY = reframe.y * scaledHeight;

    x = projectWidth / 2 - focusX;
    y = projectHeight / 2 - focusY;

    const minX = projectWidth - scaledWidth;
    const maxX = 0;
    const minY = projectHeight - scaledHeight;
    const maxY = 0;

    x = Math.min(maxX, Math.max(minX, x));
    y = Math.min(maxY, Math.max(minY, y));
  }

  return { x, y, width: scaledWidth, height: scaledHeight };
};

/**
 * Source-video crop region for thumbnail extraction (mirrors getReframeWorkspacePosition).
 *
 * @param {number} sourceWidth
 * @param {number} sourceHeight
 * @param {number} targetRatio - Output width / height (e.g. 9/16 for vertical)
 * @param {object|null} reframe - { x, y } normalised focus centre (0–1), or null
 * @returns {{ sx: number, sy: number, sw: number, sh: number }}
 */
export const getReframeCropRegion = (
  sourceWidth,
  sourceHeight,
  targetRatio,
  reframe
) => {
  let cropWidth;
  let cropHeight;
  const sourceRatio = sourceWidth / sourceHeight;

  if (sourceRatio > targetRatio) {
    cropHeight = sourceHeight;
    cropWidth = sourceHeight * targetRatio;
  } else {
    cropWidth = sourceWidth;
    cropHeight = sourceWidth / targetRatio;
  }

  let sx = (sourceWidth - cropWidth) / 2;
  let sy = (sourceHeight - cropHeight) / 2;

  if (reframe && Number.isFinite(reframe.x) && Number.isFinite(reframe.y)) {
    sx = reframe.x * sourceWidth - cropWidth / 2;
    sy = reframe.y * sourceHeight - cropHeight / 2;
    sx = Math.min(sourceWidth - cropWidth, Math.max(0, sx));
    sy = Math.min(sourceHeight - cropHeight, Math.max(0, sy));
  }

  return { sx, sy, sw: cropWidth, sh: cropHeight };
};

/**
 * Resolves reframe data for a short at a given time.
 * Per-clip reframe takes priority; falls back to short-level reframe.
 *
 * @param {object} shortData
 * @param {number} [timeAt]
 * @returns {object|null}
 */
export const getShortReframe = (shortData, timeAt) => {
  const clips = shortData?.clips;
  const shortReframe = shortData?.reframe;

  if (clips?.length > 0) {
    const time = Number.isFinite(timeAt) ? timeAt : shortData.start;
    const matchingClip = clips.find(
      (clip) => time >= clip.startTime && time < clip.endTime
    );
    const clip = matchingClip || clips[0];
    return clip?.reframe || shortReframe || null;
  }

  return shortReframe || null;
};

const getWithinRangeWorkspaceItems = (workspaceItems, start, end) => {
  const items = Object.values(workspaceItems).filter((item) => {
    const isBaseVideo = item.type === "UPVIDEO" && !item.sceneId;
    const isReframeData = item.type === "UPVIDEO" && item.sceneId;
    return !isBaseVideo && !isReframeData && ((item.videoStart >= start && item.videoEnd <= end) ||
      (item.videoStart <= start && item.videoEnd > start) ||
      (item.videoStart < end && item.videoStart > start));
  });
  items.sort((a, b) => a.videoStart - b.videoStart);
  let timelineStart = 0;
  items.forEach((item) => {
    const videoStart = Math.max(item.videoStart, start);
    const videoEnd = Math.min(item.videoEnd, end);
    const duration = videoEnd - videoStart;
    item.videoStart = videoStart;
    item.videoEnd = videoEnd;
    item.enterStart = timelineStart;
    item.enterEnd = timelineStart;
    item.exitStart = timelineStart + duration;
    item.exitEnd = timelineStart + duration;
    timelineStart += duration;
  });
  return items;
}

/**
 * Function to return the project data by modefying the id of a workspace item.
 * @param {object} projectData
 * @param {object} shortData
 * @returns An object of a new project.
 */
export const getNewProject = (projectData, shortData, reframeData) => {

  const baseVideoItem = getBaseVideoItem(projectData.workspaceItems);

  const updatedData = structuredClone(projectData);
  const updatedWorkspaceItems = {};

  const clips =
    shortData?.clips?.length > 0
      ? shortData.clips
      : [
        {
          startTime: shortData.start,
          endTime: shortData.end,
        },
      ];

  clips.forEach((element, index) => {
    element.id = `${shortData.id}_clip${index}`;
  });



  let timelineStart = 0;
  let prodDuration = 0;

  const { width: projectWidth, height: projectHeight } = projectData;

  clips.forEach((clip) => {
    const { startTime, endTime, id } = clip;
    const reframe = clip.reframe || shortData.reframe;
    const duration = endTime - startTime;
    prodDuration += duration;

    const { x, y, width, height } = getReframeWorkspacePosition(
      baseVideoItem,
      reframe,
      projectWidth,
      projectHeight
    );

    const clipItem = {
      ...structuredClone(baseVideoItem),
      id,
      x,
      y,
      width,
      height,
      videoStart: startTime,
      videoEnd: endTime,
      enterStart: timelineStart,
      enterEnd: timelineStart,
      exitStart: timelineStart + duration,
      exitEnd: timelineStart + duration
    };

    const isClipReframe = clip.isReframe || (clips.length === 1 && shortData.isReframe);
    if (isClipReframe) {
      const originalDropId = clipItem.dropId;

      // Copy subtitles from the original dropId to the new clip's id
      // so buildLocalSubtitles can find and render them for AI Reframe shorts.
      if (updatedData.subtitle?.data?.[originalDropId]) {
        if (!updatedData.subtitle.data) updatedData.subtitle.data = {};
        updatedData.subtitle.data[id] = updatedData.subtitle.data[originalDropId];
      }

      // Find scene items from the main project that belong to this clip's time range
      let sceneItems = [];
      Object.values(projectData.workspaceItems).forEach((item) => {
        if (item.type === "UPVIDEO" && item.sceneId) {
          const itemVideoStart = item.videoStart || 0;
          const itemVideoEnd = item.videoEnd || 0;
          // Check if this scene item overlaps with the clip's source time range
          if (itemVideoStart < endTime && itemVideoEnd > startTime) {
            sceneItems.push(item);
          }
        }
      });

      // If no scene items exist in main project, generate them on the fly using reframeData
      if (sceneItems.length === 0 && reframeData) {
        const { assetId } = baseVideoItem;
        // Look up by composite key first (assetId_shortId), then fall back to plain assetId
        const compositeKey = `${assetId}_${shortData.id}`;
        let assetReframe = reframeData[compositeKey] || reframeData[assetId] || (reframeData.assetId === assetId ? reframeData : null);
        if (!assetReframe && reframeData.scenes) {
          assetReframe = reframeData;
        }
        if (assetReframe && assetReframe.scenes) {
          const generatedScenes = createNewReframe(
            fromJS(baseVideoItem),
            assetReframe,
            projectWidth,
            projectHeight,
            startTime,
            endTime,
            "shorts"
          );
          if (generatedScenes) {
            sceneItems = generatedScenes.map((update) => update.newItemData).filter(Boolean);
          }
        }
      }

      if (sceneItems.length > 0) {
        clipItem.isReframe = true;
        clipItem.dropId = id;
        clipItem.reframeSrc = clip.reframeSrc || shortData.reframeSrc;

        sceneItems.sort((a, b) => (a.enterStart || 0) - (b.enterStart || 0));
        sceneItems.forEach((sceneItem) => {
          const sceneClone = structuredClone(sceneItem);

          if (!sceneClone.sceneData && reframeData) {
            const { assetId } = baseVideoItem;
            const compositeKey = `${assetId}_${shortData.id}`;
            const assetReframe = reframeData[compositeKey] || reframeData[assetId] || (reframeData.assetId === assetId ? reframeData : null);
            const sceneIdOnly = (sceneClone.sceneId || "").replace("scene", "");
            const sceneObj = assetReframe?.scenes?.find((s) => s.id === sceneIdOnly || s.sceneId === sceneIdOnly || s.scene_id === sceneIdOnly);
            if (sceneObj) {
              const updatedScene = JSON.parse(JSON.stringify(sceneObj));
              updatedScene.sceneId = sceneIdOnly;
              updatedScene.scene_id = sceneIdOnly;
              updatedScene.personKey = sceneClone.personKey;
              updatedScene.layout = sceneClone.layout;
              sceneClone.sceneData = updatedScene;
            }
          }

          const sceneVideoStart = Math.max(sceneClone.videoStart || 0, startTime);
          const sceneVideoEnd = Math.min(sceneClone.videoEnd || 0, endTime);
          const sceneDuration = sceneVideoEnd - sceneVideoStart;
          const offsetInClip = sceneVideoStart - startTime;

          const sceneIdPrefix = `${sceneClone.id || sceneClone.sceneId}_${id}`;
          sceneClone.id = sceneIdPrefix;
          sceneClone.drop = sceneIdPrefix;
          sceneClone.originalId = id; // point to the shorts clip as parent
          sceneClone.dropId = id;
          sceneClone.enterStart = timelineStart + offsetInClip;
          sceneClone.enterEnd = timelineStart + offsetInClip;
          sceneClone.exitStart = timelineStart + offsetInClip + sceneDuration;
          sceneClone.exitEnd = timelineStart + offsetInClip + sceneDuration;
          sceneClone.videoStart = sceneVideoStart;
          sceneClone.videoEnd = sceneVideoEnd;

          updatedWorkspaceItems[sceneIdPrefix] = sceneClone;
        });
      }
    }

    updatedWorkspaceItems[id] = clipItem;

    const clipBasedItems = getWithinRangeWorkspaceItems(projectData.workspaceItems, startTime, endTime);
    clipBasedItems.forEach((item) => {
      updatedWorkspaceItems[`${item.id}_${id}`] = {
        ...structuredClone(item),
        id: `${item.id}_${id}`,
        drop: `${item.id}_${id}`,
        dropId: id,
        originalId: id,
      };
    });

    timelineStart += duration;
  });

  // delete updatedWorkspaceItems[videoKey];

  updatedData.workspaceItems = updatedWorkspaceItems;
  updatedData.duration = prodDuration;
  return updatedData;
};

export const getShortsPlayerConstraints = (
  viewportWidth,
  viewportHeight,
  dimensionName
) => {
  const isCompactViewport = viewportWidth <= 767 || viewportHeight <= 500;
  if (!isCompactViewport) return null;

  const sidePadding = 32;
  const maxWidth = Math.min(viewportWidth - sidePadding, 540);

  if (dimensionName === "vertical") {
    const maxHeight = viewportHeight <= 500
      ? Math.min(Math.floor(viewportHeight * 0.5), 500)
      : 500;
    return { maxWidth, maxHeight };
  }

  if (dimensionName === "square") {
    const maxSize = viewportHeight <= 500
      ? Math.min(
        Math.floor(viewportHeight * 0.45),
        viewportWidth - sidePadding,
        534
      )
      : Math.min(viewportWidth - sidePadding, 534);
    return { maxWidth: maxSize, maxHeight: maxSize };
  }

  const maxHeight = viewportHeight <= 500
    ? Math.floor(viewportHeight * 0.4)
    : 534;
  return { maxWidth, maxHeight };
};

export const getShortsPlayerDimensions = (
  dimensionName,
  projectWidth,
  projectHeight,
  constraints = null
) => {
  let effectiveZoom;

  if (dimensionName === "vertical") {
    const maxVerticalHeight = constraints?.maxHeight ?? 500;
    const scaleToFit = maxVerticalHeight / projectHeight;
    effectiveZoom = Math.min(scaleToFit, 1);
    if (constraints?.maxWidth) {
      effectiveZoom = Math.min(effectiveZoom, constraints.maxWidth / projectWidth);
    }
  } else if (dimensionName === "square") {
    const maxSize = constraints
      ? Math.min(constraints.maxWidth ?? 534, constraints.maxHeight ?? 534)
      : 534;
    const scaleToFit = maxSize / Math.max(projectWidth, projectHeight);
    effectiveZoom = Math.min(scaleToFit, 1);
  } else {
    const maxWidth = constraints?.maxWidth ?? 540;
    const maxHeight = constraints?.maxHeight ?? 534;
    const scaleX = maxWidth / projectWidth;
    const scaleY = maxHeight / projectHeight;
    effectiveZoom = Math.min(scaleX, scaleY, 1);
  }

  return {
    effectiveZoom,
    containerWidth: Math.round(projectWidth * effectiveZoom),
    containerHeight: Math.round(projectHeight * effectiveZoom),
  };
};

/**
 * Function to get the subtitle within the range.
 * @param {number} start
 * @param {number} end
 * @param {object} subtitleDatas
 * @returns Array of subtitles within the given range.
 */
export const getSubtitlesByDuration = (start, end, subtitleDatas) => {
  return Object.values(subtitleDatas)
    .filter(
      (subtitle) =>
        (subtitle.enterStart >= start && subtitle.exitEnd <= end) ||
        (subtitle.enterStart <= start && subtitle.exitEnd > start) ||
        (subtitle.enterStart < end && subtitle.enterStart > start)
    )
    .map(({ text }) => text);
};
